import { FormValidateInput, isNotEmpty } from '@mantine/form';
import { camelCase, get, kebabCase, set, unset } from 'lodash';
import {
  Priority,
  RuleConditions,
  RuleDataForm,
  RuleDirections,
  RuleFrequencies,
  RuleItem,
  RuleTriggers,
  TimeFramesInSeconds,
} from 'models/rules';
import { validateRequired } from 'utils/validation';
import { useTranslation } from 'react-i18next';

export const useDefineRuleFormValidations = () => {
  const { t } = useTranslation();
  const initialValues: RuleDataForm = {
    formDisabled: false,
    formActionsDisabled: false,
    name: '',
    description: '',
    trigger: RuleTriggers.heartRhythmTransition,
    priority: Priority.Medium.toString(),
    frequency: RuleFrequencies.occurrence,
    data: {
      heartRhythmTransition: {
        from: [],
        to: [],
        numberOfMeasurements: { isConsecutive: false, amount: 1 },
      },
      heartRhythmMeasurement: {
        numberOfMeasurementsCondition: {
          enabled: false,
          numberOfMeasurements: {
            isConsecutive: false,
            timeWindow: TimeFramesInSeconds.hours,
            amount: 1,
          },
        },
        numberOfDaysCondition: {
          enabled: false,
          numberOfDays: {
            isConsecutive: false,
            timeWindow: TimeFramesInSeconds.days,
            amount: 1,
          },
        },
        indicatorCondition: { enabled: false, indicator: [] },
        reviewedLabelCondition: { enabled: false, reviewedLabel: [] },
        heartRateCondition: { enabled: false, heartRate: { condition: RuleConditions.above, value: 100 } },
        symptomsCondition: { enabled: false, symptoms: [] },
        symptomSeverityCondition: { enabled: false },
        activityCondition: { enabled: false, activity: [] },
        includeBadQuality: false,
      },
      physiologicalParameterChange: {
        direction: RuleDirections.decreased,
      },
      physiologicalParameterThreshold: {
        condition: RuleConditions.above,
      },
      physiologicalParameterMissing: {},
      summaryReportGenerated: { isEmpty: false, indicator: [] },
      timeSincePrescription: {},
    },
  };

  interface SetFormValuesProps {
    item: RuleItem;
    formDisabled?: boolean;
  }

  const setFormValues = ({ item, formDisabled = false }: SetFormValuesProps): RuleDataForm => {
    const template = JSON.parse(item.extras.template);
    const trigger = camelCase(template.type);
    set(initialValues, 'formDisabled', formDisabled);
    set(initialValues, 'name', item.notification.title);
    set(initialValues, 'description', item.notification.description);
    set(initialValues, 'trigger', template.type);
    set(initialValues, 'frequency', template.parameters.frequency);
    unset(template.parameters, 'frequency');
    set(initialValues, 'priority', item.notification.priority.toString());

    if (template.type === RuleTriggers.heartRhythmMeasurement) {
      Object.entries(template.parameters).forEach(([key, value]) => {
        if (key === 'includeBadQuality') {
          set(initialValues, `data.${trigger}.includeBadQuality`, value);
          return;
        }
        set(initialValues, `data.${trigger}.${key}Condition.enabled`, true);
        set(initialValues, `data.${trigger}.${key}Condition.${key}`, value);
      });
    } else {
      set(initialValues, `data.${trigger}`, template.parameters);
    }

    return initialValues;
  };

  const validateIfEnabled = (value: string | number | string[] | undefined, values: RuleDataForm, path: string) => {
    const pathParts = path.split('.');
    const trigger = kebabCase(pathParts[1]); // Extract the trigger from the path
    const isEnabled = get(values, `${pathParts.slice(0, 3).join('.')}.enabled`, true); // Check if the condition is enabled

    return validateRequired({
      value,
      enabled: values.trigger === trigger && isEnabled,
    });
  };

  const validate: FormValidateInput<RuleDataForm> = {
    name: isNotEmpty(),
    description: isNotEmpty(),
    trigger: (value) => (value === RuleTriggers.reportGenerated ? t('empty_value_select') : null),
    data: {
      heartRhythmTransition: {
        from: validateIfEnabled,
        to: validateIfEnabled,
        numberOfMeasurements: {
          amount: validateIfEnabled,
        },
      },
      heartRhythmMeasurement: {
        oneConditionEnabled: (_value, values) => {
          if (values.trigger !== RuleTriggers.heartRhythmMeasurement) return null;
          const {
            includeBadQuality: _includeBadQuality,
            oneConditionEnabled: _oneConditionEnabled,
            ...conditions
          } = values.data.heartRhythmMeasurement;
          const oneConditionEnabled = Object.values(conditions).some((condition) => condition.enabled);
          return oneConditionEnabled ? null : t('condition_enabled_label');
        },
        numberOfMeasurementsCondition: {
          numberOfMeasurements: {
            amount: validateIfEnabled,
            timeWindow: validateIfEnabled,
          },
        },
        numberOfDaysCondition: {
          numberOfDays: {
            amount: validateIfEnabled,
            timeWindow: validateIfEnabled,
          },
        },
        indicatorCondition: {
          indicator: validateIfEnabled,
        },
        reviewedLabelCondition: {
          reviewedLabel: validateIfEnabled,
        },
        heartRateCondition: {
          heartRate: {
            condition: validateIfEnabled,
            value: validateIfEnabled,
          },
        },
        symptomsCondition: {
          symptoms: validateIfEnabled,
        },
        symptomSeverityCondition: {
          symptomSeverity: validateIfEnabled,
        },
        activityCondition: {
          activity: validateIfEnabled,
        },
      },
      physiologicalParameterChange: {
        parameter: validateIfEnabled,
        direction: validateIfEnabled,
        value: validateIfEnabled,
        timeWindow: validateIfEnabled,
      },
      physiologicalParameterThreshold: {
        parameter: validateIfEnabled,
        condition: validateIfEnabled,
        threshold: validateIfEnabled,
      },
      physiologicalParameterMissing: {
        parameter: validateIfEnabled,
        timeWindow: validateIfEnabled,
      },
      summaryReportGenerated: {
        indicator: (value, values, path) => {
          const isEmptySelected = get(values, 'data.summaryReportGenerated.isEmpty', false);
          return validateIfEnabled(value, values, path) && !isEmptySelected;
        },
      },
      timeSincePrescription: {
        timeWindow: validateIfEnabled,
      },
    },
  };

  return { initialValues, validate, setFormValues };
};
